/**
 * nbn-cart-drawer.js
 * Nine by Nine Golf — Cart Drawer accordion behaviour
 *
 * The theme's own <cart-drawer> web component (in the theme JS) already
 * handles opening/closing the panel, quantity changes, notes, and section
 * re-rendering. It does NOT know about our accordion grouping, so this file
 * adds — scoped strictly to #Cart-Drawer:
 *
 *   - Accordion toggles for product groups + iron sub-groups + collapsibles
 *   - The "Expand / Collapse" all button in the drawer header
 *   - Re-binding after the theme re-renders the drawer (MutationObserver),
 *     since every quantity change / notes save / discount apply replaces the
 *     drawer's inner HTML and wipes the click handlers we attached.
 *
 * This is intentionally a self-contained IIFE that observes the document for
 * the drawer element, so it works regardless of load order relative to the
 * theme's cart-drawer definition.
 */

(function () {
  'use strict';

  const DRAWER_ID = 'Cart-Drawer';

  function getDrawer() {
    return document.getElementById(DRAWER_ID);
  }

  // ── Accordion toggles ──
  // Ignore clicks that land on an interactive control (remove button, qty
  // stepper, links, inputs) so e.g. the delete icon inside a header removes
  // the line WITHOUT also flipping the accordion open/closed.
  function isInteractiveTarget(event) {
    return !!event.target.closest(
      '.remove, .nbn-remove-btn, quantity-selector, .nbn-qty-stepper, .minus, .plus, input, a, button'
    );
  }

  function toggle(headerOrTrigger, event, allowInteractive) {
    if (event && !allowInteractive && isInteractiveTarget(event)) return;
    const body = headerOrTrigger.nextElementSibling;
    if (!body) return;
    const isOpen = headerOrTrigger.classList.contains('is-open');
    headerOrTrigger.classList.toggle('is-open', !isOpen);
    body.classList.toggle('is-open', !isOpen);
  }

  function initAccordions(root) {
    root.querySelectorAll('[data-group-toggle]').forEach((header) => {
      if (header.dataset.bound) return;
      header.dataset.bound = 'true';
      header.addEventListener('click', (event) => toggle(header, event));
    });
    root.querySelectorAll('[data-subgroup-toggle]').forEach((header) => {
      if (header.dataset.bound) return;
      header.dataset.bound = 'true';
      header.addEventListener('click', (event) => toggle(header, event));
    });
    root.querySelectorAll('.nbn-coll-trigger').forEach((trigger) => {
      if (trigger.dataset.bound) return;
      trigger.dataset.bound = 'true';
      // Collapsible triggers ARE buttons, so allow them through the guard.
      trigger.addEventListener('click', (event) => toggle(trigger, event, true));
    });
  }

  // ── Expand / Collapse all ──
  function initToggleAllButton(root) {
    const btn = document.getElementById('NbnDrawerToggleAllBtn');
    if (!btn || btn.dataset.bound) return;
    btn.dataset.bound = 'true';

    btn.addEventListener('click', () => {
      const expanding = btn.dataset.state === 'collapsed';

      root.querySelectorAll('[data-group-toggle], [data-subgroup-toggle]').forEach((header) => {
        header.classList.toggle('is-open', expanding);
        if (header.nextElementSibling) {
          header.nextElementSibling.classList.toggle('is-open', expanding);
        }
      });

      btn.dataset.state = expanding ? 'expanded' : 'collapsed';
      const label = btn.querySelector('.nbn-toggle-label');
      const icon = btn.querySelector('svg');
      if (label) label.textContent = expanding ? 'Collapse' : 'Expand';
      if (icon) {
        icon.innerHTML = expanding
          ? '<path d="M5 12l5-5 5 5"/>'
          : '<path d="M5 8l5 5 5-5"/>';
      }
    });
  }

  // ── Capture / restore open state across re-renders ──
  function captureOpenState(root) {
    const groupKeys = [];
    root.querySelectorAll('.nbn-group').forEach((group) => {
      const header = group.querySelector('[data-group-toggle]');
      if (header && header.classList.contains('is-open')) {
        groupKeys.push(group.dataset.groupKey);
      }
    });

    const subGroupKeys = [];
    root.querySelectorAll('.nbn-sub-group').forEach((subGroup) => {
      const header = subGroup.querySelector('[data-subgroup-toggle]');
      if (header && header.classList.contains('is-open')) {
        const parentGroup = subGroup.closest('.nbn-group');
        const parentKey = parentGroup ? parentGroup.dataset.groupKey : '';
        const siblingIndex = Array.from(subGroup.parentElement.children).indexOf(subGroup);
        subGroupKeys.push(`${parentKey}::${siblingIndex}`);
      }
    });

    const collapsiblesOpen = [];
    root.querySelectorAll('.nbn-coll').forEach((coll, i) => {
      const trigger = coll.querySelector('.nbn-coll-trigger');
      collapsiblesOpen[i] = !!(trigger && trigger.classList.contains('is-open'));
    });

    return { groupKeys, subGroupKeys, collapsiblesOpen };
  }

  function restoreOpenState(root, state) {
    if (!state) return;

    root.querySelectorAll('.nbn-group').forEach((group) => {
      if (state.groupKeys.indexOf(group.dataset.groupKey) !== -1) {
        const header = group.querySelector('[data-group-toggle]');
        const body = header && header.nextElementSibling;
        if (header) header.classList.add('is-open');
        if (body) body.classList.add('is-open');
      }
    });

    root.querySelectorAll('.nbn-sub-group').forEach((subGroup) => {
      const parentGroup = subGroup.closest('.nbn-group');
      const parentKey = parentGroup ? parentGroup.dataset.groupKey : '';
      const siblingIndex = Array.from(subGroup.parentElement.children).indexOf(subGroup);
      const key = `${parentKey}::${siblingIndex}`;
      if (state.subGroupKeys.indexOf(key) !== -1) {
        const header = subGroup.querySelector('[data-subgroup-toggle]');
        const body = header && header.nextElementSibling;
        if (header) header.classList.add('is-open');
        if (body) body.classList.add('is-open');
      }
    });

    root.querySelectorAll('.nbn-coll').forEach((coll, i) => {
      if (state.collapsiblesOpen[i]) {
        const trigger = coll.querySelector('.nbn-coll-trigger');
        const body = trigger && trigger.nextElementSibling;
        if (trigger) trigger.classList.add('is-open');
        if (body) body.classList.add('is-open');
      }
    });
  }

  // ── Notes save button (mirrors theme behaviour, our own markup) ──
  function initNotesSave(root) {
    const btn = document.getElementById('NbnDrawerNoteSave');
    if (!btn || btn.dataset.bound) return;
    btn.dataset.bound = 'true';
    btn.addEventListener('click', () => {
      const textarea = document.getElementById('mini-cart__notes');
      if (!textarea) return;
      fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: textarea.value }),
      });
    });
  }

  // ── Observe the drawer for re-renders and re-bind ──
  let observer = null;
  function observeDrawer(root) {
    if (observer) observer.disconnect();
    let pending = null;
    let timer = null;

    // Capture just before a change begins (theme dispatches this event)
    document.addEventListener('line-item:change:start', () => {
      const d = getDrawer();
      if (d) pending = captureOpenState(d);
    });

    observer = new MutationObserver((mutations) => {
      const structural = mutations.some((m) => m.type === 'childList');
      if (!structural) return;

      clearTimeout(timer);
      timer = setTimeout(() => {
        const d = getDrawer();
        if (!d) return;
        initAccordions(d);
        initToggleAllButton(d);
        initNotesSave(d);
        if (pending) {
          restoreOpenState(d, pending);
          pending = null;
        }
      }, 60);
    });

    observer.observe(root, { childList: true, subtree: true });
  }

  // ── Boot ──
  function boot() {
    const drawer = getDrawer();
    if (!drawer) return false;
    initAccordions(drawer);
    initToggleAllButton(drawer);
    initNotesSave(drawer);
    observeDrawer(drawer);
    return true;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      if (!boot()) {
        // Drawer may be injected later; watch for it once.
        const bodyObserver = new MutationObserver(() => {
          if (boot()) bodyObserver.disconnect();
        });
        bodyObserver.observe(document.body, { childList: true, subtree: true });
      }
    });
  } else {
    if (!boot()) {
      const bodyObserver = new MutationObserver(() => {
        if (boot()) bodyObserver.disconnect();
      });
      bodyObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

})();